import React, { CSSProperties, useMemo } from 'react';
import type { KitTextLocale } from 'fym-shared/src/music24/kitText';
import { themeColours, themeType, type ReleaseKitLook } from 'fym-shared/src/music24/releaseThemes';
import {
  AI_REEL_LAYOUT,
  aiReelChipText,
  aiReelFootageColours,
  aiReelHookTitlePx,
} from 'fym-shared/src/music24/story/aiReelVideo';
import { storyFrameOf } from 'fym-shared/src/music24/story/storyFrames';
import { storyAlpha, storyPillStyle, type StoryColours } from 'fym-shared/src/music24/story/storyStyles';
import { aiReelWatermark } from 'fym-shared/src/music24/story/watermark';
import { TypedMemo } from '../../../../common/types';
import { kitFontName, useKitWebFonts } from '../../themes/kitWebFonts';
import { utcToday } from './studioStory';
import { WatermarkLockup } from './WatermarkLockup';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio frames; each frame uses its own slice of it */
import styles from './StoryPreview.module.scss';
/* eslint-enable css-modules/no-unused-class */

const FULL: CSSProperties = { position: 'absolute', inset: 0 };

interface AiReelPreviewProps {
  look: ReleaseKitLook;
  palette: readonly string[];
  title: string;
  artist: string;
  /** `YYYY-MM-DD`; null with no date: no chip. */
  releaseDate: string | null;
  /** The cover: it stands in for the bank clip (the preview has no footage). */
  artworkUrl: string | undefined;
  /** Free plans: the watermark of every frame. */
  watermark: boolean;
  width: number;
  /** Today as `YYYY-MM-DD` (UTC). Absent: today. */
  today?: string;
  /** The language of the release chip, as the render writes it; absent: English. */
  locale?: KitTextLocale;
}

/**
 * The step-3 preview of the AI reel (design `asset-redesign/z6`): the first frame of the reel, drawn
 * with the tables of the render (fym-shared `aiReelVideo.ts`): the footage full bleed with no colour
 * wash (the cover stands in for the bank clip), the scrim of the text band, the hook card (artist
 * over the title in the display type, fitted to 780 px), the release chip ("Out Friday") and, on the
 * Free plan, the watermark at its place of every frame.
 */
const AiReelPreviewComponent: React.FC<AiReelPreviewProps> = (props) => {
  const { look, palette, title, artist, releaseDate, artworkUrl, watermark, width, today, locale } = props;
  useKitWebFonts();
  const { themeId } = look;
  const type = useMemo(() => themeType(look), [look]);
  const colors = useMemo<StoryColours>(() => {
    const colours = themeColours(themeId, palette);
    return {
      ink: colours.background,
      surface: colours.surface,
      text: colours.text,
      accent: colours.accent,
      onAccent: colours.onAccent,
    };
  }, [palette, themeId]);
  const footage = useMemo(() => aiReelFootageColours(colors), [colors]);
  const frame = storyFrameOf(themeId);
  const chip = aiReelChipText(releaseDate, today ?? utcToday(), locale);
  const titlePx = aiReelHookTitlePx({
    title,
    font: type.fonts.display,
    style: type.display,
    displayFactor: frame.factors.display * type.display.scale,
  });
  const { hook, scrim } = AI_REEL_LAYOUT;
  const scale = width / 1080;
  const shadow = `0 2px 18px ${storyAlpha(footage.dark, 0.8)}`;
  const scrimStops = scrim.map(
    (stop) => `${storyAlpha(footage.dark, stop.alpha)} ${((stop.y / 1920) * 100).toFixed(2)}%`
  );
  const font = (fontId: Parameters<typeof kitFontName>[0], style: typeof type.text): CSSProperties => ({
    fontFamily: kitFontName(fontId),
    fontWeight: style.weight,
    fontStyle: style.italic ? 'italic' : 'normal',
    textTransform: style.case === 'none' ? 'none' : style.case,
    letterSpacing: `${style.tracking}em`,
  });

  return (
    <div className={styles.frame} style={{ width, height: 1920 * scale }} data-testid="ai-reel-preview">
      <div
        className={styles.canvas}
        style={{ width: 1080, height: 1920, transform: `scale(${scale})`, background: colors.ink }}
      >
        {artworkUrl === undefined ? (
          <div style={{ ...FULL, background: colors.surface }} />
        ) : (
          <img src={artworkUrl} alt="" style={{ ...FULL, width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
        <div
          style={{
            ...FULL,
            background: `linear-gradient(180deg, ${storyAlpha(footage.dark, 0)} 0%, ${scrimStops.join(', ')}, ${storyAlpha(footage.dark, 0)} 100%)`,
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: hook.x,
            top: hook.top,
            width: hook.w,
            height: hook.bottom - hook.top,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: hook.gap,
            color: footage.light,
            textShadow: shadow,
            textAlign: 'center',
          }}
        >
          <div
            style={{ ...font(type.fonts.text, type.text), fontSize: Math.round(hook.artistPx * frame.factors.text) }}
          >
            {artist}
          </div>
          <div
            style={{
              ...font(type.fonts.display, type.display),
              fontSize: titlePx,
              lineHeight: 0.92,
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </div>
        </div>
        {chip !== null && (
          <div
            data-testid="ai-reel-preview-chip"
            style={{
              position: 'absolute',
              left: AI_REEL_LAYOUT.chip.x,
              top: AI_REEL_LAYOUT.chip.y,
              height: AI_REEL_LAYOUT.chip.height,
              boxSizing: 'border-box',
              padding: `0 ${AI_REEL_LAYOUT.chip.paddingX}px`,
              borderRadius: 999,
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              ...storyPillStyle(themeId, frame, colors),
              ...font(type.fonts.text, type.text),
              fontWeight: 700,
              fontSize: Math.round(AI_REEL_LAYOUT.chip.textPx * frame.factors.text),
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                flex: 'none',
                width: AI_REEL_LAYOUT.chip.dotPx,
                height: AI_REEL_LAYOUT.chip.dotPx,
                borderRadius: '50%',
                background: (storyPillStyle(themeId, frame, colors).color as string | undefined) ?? colors.onAccent,
              }}
            />
            {chip}
          </div>
        )}
        {watermark && (
          // Design w0: the glass stack at the foot of the safe column, on every frame of the reel.
          <WatermarkLockup
            testId="ai-reel-preview-watermark"
            lockup={aiReelWatermark().lockup}
            tone="dark"
            glass
            x={aiReelWatermark().x}
            y={aiReelWatermark().y}
          />
        )}
      </div>
    </div>
  );
};

export const AiReelPreview = TypedMemo(AiReelPreviewComponent);
